import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import cn from 'classnames'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import HideIcon from 'components/Icons/Hide'
import ShowIcon from 'components/Icons/Show'

import m from './messages'
import PasswordStatus from './PasswordStatus'
import {
  validateAtLeastNCharactersLong,
  validateOneLowercaseCharacter,
  validateOneNumber,
  validateOneSpecialCharacter,
  validateOneUppercaseCharacter,
} from './validatePassword'

type PasswordFieldProps = TextFieldProps & {
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  fullWidth?: boolean
  showStatus?: boolean
  strongPassword?: boolean
  value?: string
}

function PasswordField({
  children,
  className,
  disabled,
  showStatus = true,
  slotProps: slotPropsFromParent,
  strongPassword = true,
  value,
  ...restProps
}: PasswordFieldProps) {
  const { formatMessage: f } = useIntl()
  const [hidePassword, setHidePassword] = useState(true)
  const parentInputSlot =
    slotPropsFromParent?.input && typeof slotPropsFromParent.input === 'object'
      ? slotPropsFromParent.input
      : undefined
  const testId =
    (slotPropsFromParent?.htmlInput as Record<string, string> | undefined)?.[
      'data-test-id'
    ] || 'password'

  return (
    <>
      <TextField
        {...restProps}
        className={cn(showStatus && '-mb3', className)}
        disabled={disabled}
        slotProps={{
          ...slotPropsFromParent,
          htmlInput: {
            autoComplete: 'off',
            ...slotPropsFromParent?.htmlInput,
          },

          input: {
            ...parentInputSlot,
            endAdornment: (
              <InputAdornment position="end">
                {parentInputSlot?.endAdornment}
                <IconButton
                  data-test-id={`${testId}.toggleVisibility`}
                  disabled={disabled}
                  onClick={() => setHidePassword(!hidePassword)}
                  size="large"
                >
                  {hidePassword ? <ShowIcon /> : <HideIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        type={hidePassword ? 'password' : 'text'}
        value={value}
      />

      {children}

      {showStatus && (
        <>
          <PasswordStatus
            data-test-id={`${testId}.status.oneLowercaseCharacter`}
            success={validateOneLowercaseCharacter(value)}
            text={f(m.oneLowercaseCharacter)}
          />

          {strongPassword && (
            <PasswordStatus
              data-test-id={`${testId}.status.oneUppercaseCharacter`}
              success={validateOneUppercaseCharacter(value)}
              text={f(m.oneUppercaseCharacter)}
            />
          )}

          <PasswordStatus
            data-test-id={`${testId}.status.oneNumber`}
            success={validateOneNumber(value)}
            text={f(m.oneNumber)}
          />

          {strongPassword && (
            <PasswordStatus
              data-test-id={`${testId}.status.oneSpecialCharacter`}
              success={validateOneSpecialCharacter(value)}
              text={f(m.oneSpecialCharacter)}
            />
          )}

          <PasswordStatus
            data-test-id={`${testId}.status.atLeast12CharactersLong`}
            success={validateAtLeastNCharactersLong(
              value,
              strongPassword ? 12 : 8
            )}
            text={f(m.atLeastNCharactersLong, {
              n: strongPassword ? 12 : 8,
            })}
          />
        </>
      )}
    </>
  )
}

export default PasswordField
